import {
  BottomBar,
  ButtonRedirection,
  HangUpPopup,
  HeaderPopup,
  Icons,
  IconWithTextComponent,
  LeftButtonConferenceBottomBar,
  Popup,
  ReactIcons,
} from "@kalyzee/escaet-ui";
import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  isSpectator,
  MeetingContainerAPI,
  MeetingSessionOvenMediaReceiver,
  MeetingSourceDeviceType,
  MeetingSourceElement,
  MeetingSourceElementRef,
  useMeetingAudioMixer,
  useOrderedMeetingSessions,
} from "@kalyzee/kast-web-sdk";
import { audioContextIsReadyToUse, awaitWithMinimumDelay, delay, Loading, Touchable, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import {
  MeetingRoomAudienceType,
  MeetingRoomHighlightingState,
  MeetingRoomStatus,
  MeetingSessionAddMedia,
  MeetingSessionHighlightingState,
  MeetingSessionStatus,
  MeetingState,
  MeetingUpdateByIdRequest,
  MeetingUpdateByIdResult,
} from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BottomPanelWebrtcSessionStats } from "../../../../components/debug/BottonPanelWebrtcSessionStats";
import { GoTo } from "../../../../components/navigation/GoTo";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import PauseView from "../../../../components/utils/PauseView";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { closeMeetingActivityRoom, stopRoomHighlight, stopSessionHighlight } from "../../../../helpers/meeting";
import { appMessage } from "../../../../helpers/message";
import { addPopup, removePopup, removePopupByGroup } from "../../../../helpers/popup";
import { getExpertHomeParams } from "../../../../helpers/storage";
import { toastError } from "../../../../helpers/toast";
import { expertWebcamName } from "../../../../helpers/webcam";
import {
  MeetingSourceExtended,
  useEmptyMediaStream,
  useMeetingConfigurator,
  useMeetingContainerAPI,
  useMeetingSelector,
  useMeetingSources,
  useMultiMeetingAPI,
  useStandaloneHighlightedMeetingSessions,
  useMeetingStateUpdater,
} from "../../../../hooks/meeting";
import { useMeetingMasterMessagingBridge } from "../../../../hooks/meetingMessagingBridge";
import { useActivityData, useExpertAvailableViews, usePopulatedMeetingData } from "../../../../hooks/populatedData";
import { useSettings } from "../../../../hooks/settings";
import { ExpertView } from "../../../../interfaces/populatedData";
import { SessionMode } from "../../../../store/session/slices";
import { useExpertContext } from "../context";
import "./meeting.css";
import styles from "./meeting.module.css";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { useGroupsHighlightRequestsList, useUsersHighlightRequestsList } from "../../../../components/dashboard/HighlightRequestsList";

// ---------------------------------------------------------------------- //
// ---------------------------   TODO ----------------------------- //
// ---------------------------------------------------------------------- //
// 1) on the backend, implement the highlight rooms, we have to allow enlistment in it if
//    the user has an highlited sessions or belong to an highlited group
// 2) on the frontend, use the same logic than with groups, first we retrieve the candidates,
//    then we only keep the ones that enlisted
// 3) use the useActivityData hook on the user page and enjoy
// ---------------------------------------------------------------------- //
// ---------------------------   TODO ----------------------------- //
// ---------------------------------------------------------------------- //

export const MeetingExpertPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);


  const meetingId = pageContextRef.current?.params?.id;

  const multiMeetingAPI = useMultiMeetingAPI();
  const meetingAPI = useMeetingContainerAPI(meetingId);
  const meetingAPIRef = useRefAutoUpdate(meetingAPI);

  useMeetingConfigurator(meetingAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.extra?.type === "camera") {
            const maxBitrate =
              mediaSession.name === expertWebcamName
                ? settingsRef.current?.debug?.maxBitrate?.expert?.webcam ?? DEFAULT_MAX_BITRATES.expert.webcam
                : settingsRef.current?.debug?.maxBitrate?.expert?.camera ?? DEFAULT_MAX_BITRATES.expert.camera;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
                },
              },
            };
          }
          if (mediaSession.extra?.type === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.expert?.screen ?? DEFAULT_MAX_BITRATES.expert.screen;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
                },
              },
            };
          }
          return undefined;
        },
      },
      reception: {
        options: () => ({
          audio: false,
          video: { mode: "subscription", delayToDisableStream: 5000 },
        }),
      },
    },
  });
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  const [meeting] = useMeetingSelector(meetingAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(meetingAPI, (state) => state.session);
  const [rooms] = useMeetingSelector(meetingAPI, (state) => state.rooms);
  const [emissionSessions] = useMeetingSelector(meetingAPI, (state) => state.emissionSessions);
  const [receptionSessions] = useMeetingSelector(meetingAPI, (state) => state.receptionSessions);

  const sources = useMeetingSources();

  const expertCtx = useExpertContext();
  const expertCtxRef = useRefAutoUpdate(expertCtx);
  const sourcesRef = useRef<{ [key: string]: React.RefObject<MeetingSourceElementRef> }>({});
  const isStoppingRef = useRef(false);

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    return isSpectator(session?.role) ? [] : ((sources ?? []) as any);
  }, [sources]);

  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const orderedSessions = useOrderedMeetingSessions(meeting, (_, session) => {
    if (!sessionRef.current) return false;
    if (isSpectator(session.role)) return false;
    if (session.status !== MeetingSessionStatus.RUNNING) return false;
    return session.id !== sessionRef.current?.id;
  });

  useMeetingMasterMessagingBridge();

  const populatedData = usePopulatedMeetingData(meetingAPI);
  const activityData = useActivityData(populatedData);
  const availableViews = useExpertAvailableViews(populatedData, activityData, expertCtx?.spiedGroupId);

  const currentView = availableViews[0];

  const isVisibleInGroups = ["activity_one_group", "activity_one_group_spy", "activity_one_leader", "activity_all_leaders"].includes(currentView?.type);

  const darkMode = (isVisibleInGroups && pageContextRef.current?.page === PageMasterMode.MEETING_GROUP) || currentView?.type === "activity_pedagogy";

  
  const usersHighlightRequestsList = useUsersHighlightRequestsList(meetingAPI);
  const groupsHighlightRequestsList = useGroupsHighlightRequestsList(meetingAPI);
  const { ongoingGroups } = useMemo(() => {
    const ongoing = populatedData?.rooms.groups.filter((group) => group.room.status === MeetingRoomStatus.Ongoing) ?? [];
    return { ongoingGroups: ongoing };
  }, [populatedData]);

  const emptyMediaStream = useEmptyMediaStream(session ? true : false);

  const { update: updateMeetingState } = useMeetingStateUpdater(meetingAPI);

  useEffect(() => {
    const webcamMediaSession = session?.medias?.find((m) => m.name === expertWebcamName);
    if (!webcamMediaSession) return;
    const webcam = expertCtx?.webcam;
    const webcamviews: ExpertView["type"][] = ["highlight", "activity_one_group", "activity_one_leader", "activity_all_leaders", "activity_pedagogy"];
    const enableWebcam = currentView?.type && webcamviews.includes(currentView.type);
    const api = meetingAPIRef.current;
    const mediaStream = enableWebcam ? webcam?.mediaStream : emptyMediaStream;
    api?.setMediaStream(webcamMediaSession.id, mediaStream);
  }, [session, expertCtx?.webcam, currentView, emptyMediaStream]);

  const canHearSession = useCallback(
    (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
      if (!currentView) return false;

      const sessionStatus = session.data.session?.status;
      const sessionId = session.data.session?.id;
      if (!sessionId) return false;
      if (sessionStatus !== MeetingSessionStatus.RUNNING) return false;

      const studentData = currentView.students.find((data) => data.session.id === sessionId);
      if (studentData) return studentData.audio;

      const expertData = currentView.experts.find((data) => data.session.id === sessionId);
      if (expertData) return expertData.audio;

      return false;
    },
    [currentView]
  );

  const audioMixer = useMeetingAudioMixer(getAudioContextForAudioMixer(), meetingAPI, true, canHearSession, settings.debug?.audio?.mixer);
  if (settings.debug?.audio?.mixer) (window as any).audioMixer = audioMixer;

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const stop = async (): Promise<boolean> => {
    pageContextRef.current?.setLoading(true);
    await delay(200);
    const result = await updateMeetingState(MeetingState.STOPPED);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.STOPPED) {
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return true;
    }
    toastError("Une erreur est apparue.");
    return false;
  };

  const join = async (id: string) => {
    const api = multiMeetingAPIRef.current;
    if (!api.all) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }

    const container = api.all.getOrCreateContainer(id);
    const containerAPI = container.getAPI();

    const medias: MeetingSessionAddMedia<any, any, any>[] = (sources as MeetingSourceExtended[]).map((s) => {
      const type = s.extra.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      return {
        video: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.videoIsEnabled(group),
        },
        audio: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.audioIsEnabled(group),
        },
        name: s.name,
        deviceId: s.id,
        extra: {
          type,
          label: s.name,
          controls: s.controls?.type,
        },
      };
    });

    medias.push({
      video: {
        deviceType: MeetingSourceDeviceType.WEB,
        enabled: containerAPI.emissionManager.videoIsEnabled("cameras"),
      },
      audio: {
        deviceType: MeetingSourceDeviceType.WEB,
        enabled: containerAPI.emissionManager.audioIsEnabled("cameras"),
      },
      name: expertWebcamName,
      extra: { type: "camera" },
    });

    const response = await containerAPI.join({
      medias,
    });
    if (response.error) {
      console.error(`[ERROR] join: `, response.error);
      toastError(`Une erreur est apparue.`);
      container.destroy();
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }
    const meeting = response.response?.meeting;
    if (!meeting) {
      console.error(`[ERROR] join - internal error: meeting is undefined`);
      toastError(`Une erreur est apparue.`);
      container.destroy();
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }
    const session = response.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      container.destroy();
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }
  };

  const leave = async () => {
    expertCtxRef.current?.setAudioStreamEnabled(false);
    const api = meetingAPIRef.current;
    if (!api) return;
    await api.leave();
  };

  const updateMedias = useCallback(async () => {
    if (!meetingAPI) return;
    const manager = meetingAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let screensVideoIsEnabled = true;
      let audioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "screen" || m.extra?.type === undefined) screensVideoIsEnabled = screensVideoIsEnabled && m.video.enabled;
        audioIsEnabled = audioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await meetingAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await meetingAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!screensVideoIsEnabled && manager.videoIsEnabled("screens")) {
        await meetingAPI.disableVideo({ direction: "outgoing", group: "screens" });
      } else if (screensVideoIsEnabled && !manager.videoIsEnabled("screens")) {
        await meetingAPI.enableVideo({ direction: "outgoing", group: "screens" });
      }
      if (!audioIsEnabled && manager.audioIsEnabled()) {
        await meetingAPI.mute();
      } else if (audioIsEnabled && !manager.audioIsEnabled()) {
        await meetingAPI.unmute();
      }
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    expertCtxRef.current?.setCamerasVideoStreamEnabled(manager.videoIsEnabled("cameras"));
    expertCtxRef.current?.setScreensVideoStreamEnabled(manager.videoIsEnabled("screens"));
    expertCtxRef.current?.setAudioStreamEnabled(manager.audioIsEnabled());
  }, [audioMixer, expertCtxRef, meeting, meetingAPI, session]);

  const onSendBackToSeats = async () => {
    if (activityData?.mode !== "highlight") return;

    const groups = populatedData?.rooms.groups;

    activityData.sessions?.forEach((session) => {
      if (session.session.highlighting === MeetingSessionHighlightingState.ONGOING) {
        stopSessionHighlight(session.session.id);
      }
    });

    // we check groups too
    groups?.forEach((group) => {
      if (group.room.highlighting === MeetingRoomHighlightingState.ONGOING) {
        stopRoomHighlight(group.room.id);
      }
    });
  };

  const onLeaveGroupVisibilityMode = async () => {
    const meetingId = meeting?.id;
    if (!meetingId) return;
    expertCtx?.setSpiedGroupId(undefined);
    if (populatedData?.rooms.activityRoom?.room.status !== MeetingRoomStatus.Closed) {
      closeMeetingActivityRoom(meetingId);
    }
    /**
     * TODO :
     * Scnenario where we want to come back to the group we were at :
     * - we need to add a parameter "maintainEnlistedUsers" on the backend so we can update the room and keep the users
     * - we need to memorize on frontend in a state the fact we were in group mode before
     */
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // IS NOW DONE AUTOMATICALLY ON THE BACKEND :

  // useEffect(() => {
  //   // there is no ongoing highlighted sessions, but perhaps
  //   // some groups with no online users anymore  are still in highlight mode,
  //   // we also turn them off so that we will not suddenly switch to the highlight view if
  //   // if one of those group user comes back

  //   // TODO : to improve because currently, if all users of an highlighted group
  //   // leave but there is still some other highlighted sessions, the group highlightment
  //   // status will not be updated
  //   const ongoingGroups = populatedData?.rooms.groups.filter(group =>
  //     group.room.highlighting === MeetingRoomHighlightingState.ONGOING
  //   );
  //   ongoingGroups?.forEach(group => {
  //     const anyOnline = group.users.some(u => u.sessions.length);
  //     if (!anyOnline) stopRoomHighlight(group.id);
  //   });
  // }, [populatedData?.rooms.groups, rooms]);

  useEffect(() => {
    const state = meeting?.state;

    let currentPopup: React.ReactNode;
    const showMeetingStoppedPopup = () => {
      if (isStoppingRef.current) return;
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
      currentPopup = (
        <Popup
          style={{
            width: "fit-content",
            height: "fit-content",
            padding: "2em",
          }}
          Header={<HeaderPopup icon={Icons.Raccrocher} title="Le cours est terminé" />}
          Content={
            <ButtonRedirection
              className={styles.buttonPopupRestart}
              buttons={[
                {
                  text: "Revenir à la liste des cours",
                  icon: Icons.LargeHangup,
                  label: "Cliquer pour revenir en arrière",
                  onClick: () => {
                    removePopup(currentPopup);
                    removePopupByGroup("main"); // clean all popups
                    pageContextRef.current?.goTo(PageMasterMode.LIST);
                  },
                },
                {
                  text: "Relancer le cours",
                  icon: Icons.Play,
                  label: "Cliquer pour relancer le cours",
                  onClick: async () => {
                    await start();
                    removePopup(currentPopup);
                  },
                },
              ]}
            />
          }
        />
      );
      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.IDLE) {
      // first launch
      const defaultVolume = settingsRef.current?.defaultVolumeAtStart;
      if (defaultVolume !== undefined && defaultVolume >= 0 && defaultVolume <= 100) {
        expertCtxRef.current?.setVolume(defaultVolume); // set default value
      }
      start();
    } else if (state === MeetingState.STOPPED) {
      showMeetingStoppedPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state]);

  useEffect(() => {
    emissionSessions?.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group) s.data.groups = [group];
    });
  }, [emissionSessions]);

  useEffect(() => {
    if (!expertCtx) return;
    const refs = Object.values(sourcesRef.current);
    refs.forEach((r) => {
      const sourceElementRef = r.current;
      if (!sourceElementRef) return;
      const source = usableSources.find((s) => s === sourceElementRef.getData()?.source);
      if (!source) return;
      sourceElementRef.enableAudio(expertCtx.audioStreamEnabled);
      if (source.extra.type === "camera") sourceElementRef.enableVideo(expertCtx.camerasVideoStreamEnabled);
      if (source.extra.type === "screen") sourceElementRef.enableVideo(expertCtx.screensVideoStreamEnabled);
    });
  }, [expertCtx, usableSources]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = meetingAPI;
  }, [settings, audioMixer, meetingAPI]);

  useEffect(() => {
    expertCtxRef.current?.setOrderedSessions(orderedSessions);
  }, [orderedSessions]);

  useEffect(() => {
    expertCtxRef.current?.setSyncMicrophone(true);
    const id = pageContextRef.current?.params?.id ?? "";
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        join(id);
      }
    });
    const beforeGoToListener = pageContextRef.current?.addEventListener("onBeforeGoto", () => {
      removePopupByGroup("main"); // Clean popup asap
    });
    return () => {
      expertCtxRef.current?.setSyncMicrophone(false);
      expertCtxRef.current?.setOrderedSessions([]);
      removePopupByGroup("main");
      beforeGoToListener?.flush();
      leave();
    };
  }, []); 

  useEffect(() => expertCtx?.setPopulatedMeetingData(populatedData), [populatedData, expertCtx]);
  useEffect(() => expertCtx?.setAvailableViews(availableViews), [availableViews, expertCtx]);
  useEffect(() => expertCtx?.setCurrentView(currentView), [currentView, expertCtx]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meetingAPI || !meeting || !session || !rooms) return <Loading fullscreen message="Chargement en cours" />;

  const renderOutletOrRedirect = () => {
    const currentPage = pageContextRef.current?.page;
    if (!currentPage) return null;

    const allowedPages: PageMasterMode[] = [];
    switch (currentView.type) {
      case "highlight":
        allowedPages.push(PageMasterMode.MEETING_DASHBOARD);
        break;
      case "activity_pedagogy":
        allowedPages.push(PageMasterMode.MEETING_PEDAGOGY);
        break;
      case "activity_one_group":
      case "activity_one_group_spy":
      case "activity_one_leader":
      case "activity_all_leaders":
      case "groups_enlistment":
        allowedPages.push(PageMasterMode.MEETING_GROUP);
        break;
      case "normal":
        allowedPages.push(PageMasterMode.MEETING_DASHBOARD);
        allowedPages.push(PageMasterMode.MEETING_GROUP);
        allowedPages.push(PageMasterMode.MEETING_CALL);
        allowedPages.push(PageMasterMode.MEETING_SURVEY);
        break;
      case "groups_overview":
        allowedPages.push(PageMasterMode.MEETING_DASHBOARD);
        allowedPages.push(PageMasterMode.MEETING_CALL);
        allowedPages.push(PageMasterMode.MEETING_SURVEY);
        break;
    }

    if (currentPage !== PageMasterMode.MEETING && !allowedPages.includes(currentPage)) {
      return <GoTo page={allowedPages[0]} params={{ id: pageContextRef.current?.params?.id }} />;
    } else {
      return <Outlet />;
    }
  };

  const renderLeftButtonBottomBar = (): JSX.Element => {
    if (pageContext?.page === PageMasterMode.MEETING_DASHBOARD) {
      const start = meeting?.startedAt ? new Date(meeting?.startedAt) : undefined;
      if (currentView?.type === "highlight") {
        const pendingRequests = ongoingGroups.length ? groupsHighlightRequestsList.groups.length : usersHighlightRequestsList.users.length;
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              alignItems: "stretch",
            }}
          >
            <IconWithTextComponent
              text="Renvoyer à leurs places"
              style={{
                backgroundColor: "#F1EFEB",
                color: "black",
                padding: "1em 2em",
                gap: "0.5em",
                fontSize: "0.8em",
              }}
              onClick={onSendBackToSeats}
            />
            <IconWithTextComponent
              text={`${pendingRequests}`}
              icon={ReactIcons.IcoHand}
              style={{
                backgroundColor: pendingRequests ? "#0090FF" : "#22222240",
                color: "white",
                padding: "1em 1.5em",
                gap: "0.5em",
                fontSize: "0.8em",
              }}
              onClick={() => expertCtx?.switchHighlitghedRequestsPanelOpen()}
            />
          </div>
        );
      }
      return (
        <LeftButtonConferenceBottomBar
          onToggleCallHangUp={() => {
            const overlay = (
              <HangUpPopup
                startCourseDate={start}
                onNext={async () => {
                  removePopup(overlay);
                  await stop();
                }}
                onClose={() => {
                  removePopup(overlay);
                }}
                onPerformExitCall={meeting.endCallEnabled && !meeting.endCallWasDone ? () => {
                  removePopup(overlay);
                  pageContextRef.current?.goTo(PageMasterMode.MEETING_CALL, { id: meeting?.id }, { queries: { exit: "true" }});
                } : undefined}
              />
            );
            addPopup(overlay, "meeting-hangup-popup", "main");
          }}
          onToggleCallPause={() => {
            updateMeetingState(MeetingState.PAUSED);
          }}
          date={start ?? new Date()}
        />
      );
    }
    return <div />;
  };

  const renderBackButton = () => {
    if (pageContextRef.current?.page === PageMasterMode.MEETING_DASHBOARD || pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
      return undefined;
    }

    if (isVisibleInGroups) {
      // User is in visibility mode, forced to be on the meeting group page,
      // Now he wants out, we need to remove the visiblity mode and he'll be automatically
      // redirected to dashboard
      let text = "Quitter le groupe";

      const activityRoom = populatedData?.rooms.activityRoom?.room;
      const audience = activityRoom?.audience;

      if (audience?.type === MeetingRoomAudienceType.RoomLeader) {
        if (audience.values === "*") {
          text = "Renvoyer à leurs groupes";
        } else {
          text = "Renvoyer à son groupe";
        }
      }

      return (
        <Touchable
          className="text-[#F1EFEB] px-8 rounded-full h-full bg-[#696969] text-center flex justify-between items-center font-bold gap-2"
          onClick={onLeaveGroupVisibilityMode}
        >
          <ReactIcons.PedagoArrowLeft style={{ width: 20, color: "#F1EFEB" }} />
          {text}
        </Touchable>
      );
    }

    if (currentView?.type === "groups_enlistment") {
      // If we are on group page and in groups_enlistment mode, only way to
      // go back to dashboard is to validate/open the groups
      return undefined;
    }

    return (
      <Touchable
        className="px-8 rounded-full h-full bg-gray-200 text-center flex justify-between items-center font-bold gap-2"
        onClick={() => {
          pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current.params?.id });
        }}
      >
        <ReactIcons.PedagoArrowLeft style={{ width: 20 }} />
        Retour à l'accueil
      </Touchable>
    );
  };

  const renderBottomBar = (darkMode = false) => {
    const style: React.CSSProperties = {};
    const renderSource = (source: MeetingSourceExtended) => {
      if (!sourcesRef.current[source.name!]) sourcesRef.current[source.name!] = createRef();
      const srcType = source.extra?.type;
      return (
        <MeetingSourceElement
          className="meeting-source-expert"
          style={{ height: "100%", width: "100%", border: "unset" }}
          ref={sourcesRef.current[source.name!]}
          key={source.name}
          source={source}
          using
          audioContext={getAudioContextForVolumeAnalyzer()}
          callbacks={{
            track: async (track, data) => {
              const api = meetingAPIRef.current;
              if (!api) return;
              const mediaSession = session?.medias?.find((m) => m.name === source.name);
              if (!mediaSession) return;
              const media = data.media;
              await api?.setMediaStream(mediaSession.id, media);
            },
            error: (type) => {
              if (type === "audio") {
                toastError("Veuillez autoriser le navigateur à accéder au microphone");
                meetingAPIRef.current?.mute();
              }
              if (type === "video") {
                toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                if (srcType === "screen") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "screens" });
                if (srcType === "camera") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "cameras" });
              }
            },
          }}
        />
      );
    };
    const cameras = usableSources
      .filter((s) => s.extra.type === "camera")
      .map((s) => {
        const src = renderSource(s);
        return { content: src, id: src.key ?? "" };
      });
    const screens = usableSources
      .filter((s) => s.extra.type === "screen")
      .map((s) => {
        const src = renderSource(s);
        return { content: src, id: src.key ?? "" };
      });
    return (
      <div className={styles.bottomBarContainer} style={style}>
        <BottomBar
          isDark={darkMode}
          leftButton={renderLeftButtonBottomBar()}
          volume={`${expertCtx?.volume}`}
          cameras={isSpectator(session) || !cameras?.length ? undefined : cameras}
          sharedScreens={isSpectator(session) || !screens?.length ? undefined : screens}
          microphoneIsActive={expertCtx?.audioStreamEnabled}
          camerasAreActive={expertCtx?.camerasVideoStreamEnabled}
          sharedScreensAreActive={expertCtx?.screensVideoStreamEnabled}
          onToggleMicrophone={async () => {
            const manager = meetingAPI.emissionManager;
            let success = true;
            if (manager.audioIsEnabled()) {
              success = (await meetingAPI.mute()).success;
            } else {
              success = (await meetingAPI.unmute()).success;
            }
            if (!success) toastError("Une erreur est survenue...");
          }}
          onToggleCameras={async () => {
            const manager = meetingAPI.emissionManager;
            let success = true;
            if (manager.videoIsEnabled("cameras")) {
              success = (await meetingAPI.disableVideo({ direction: "outgoing", group: "cameras" })).success;
            } else {
              success = (await meetingAPI.enableVideo({ direction: "outgoing", group: "cameras" })).success;
            }
            if (!success) toastError("Une erreur est survenue...");
          }}
          onToggleShareScreens={async () => {
            const manager = meetingAPI.emissionManager;
            let success = true;
            if (manager.videoIsEnabled("screens")) {
              success = (await meetingAPI.disableVideo({ direction: "outgoing", group: "screens" })).success;
            } else {
              success = (await meetingAPI.enableVideo({ direction: "outgoing", group: "screens" })).success;
            }
            if (!success) toastError("Une erreur est survenue...");
            expertCtx?.setScreensVideoStreamEnabled(manager.videoIsEnabled("screens"));
          }}
          selectedCameraIndex={currentCameraIndex}
          selectedSharedScreenIndex={currentScreenIndex}
          onMuteVolume={() => {
            expertCtx?.setVolume(0);
          }}
          onMaximumVolume={() => {
            expertCtx?.setVolume(100);
          }}
          onVolumeChange={(e) => {
            const volume = Number(e.target.value);
            expertCtx?.setVolume(volume);
          }}
          onVolumeSliderFocus={(e) => expertCtx?.setVolumeSliderFocused(e)}
          onNextCamera={() => {
            let newIndex = currentCameraIndex + 1;
            if (newIndex >= cameras.length) newIndex = cameras.length - 1;
            setCurrentCameraIndex(newIndex);
          }}
          onPreviousCamera={() => {
            let newIndex = currentCameraIndex - 1;
            if (newIndex < 0) newIndex = 0;
            setCurrentCameraIndex(newIndex);
          }}
          onNextSharedScreen={() => {
            let newIndex = currentScreenIndex + 1;
            if (newIndex >= screens.length) newIndex = screens.length - 1;
            setCurrentScreenIndex(newIndex);
          }}
          onPreviousSharedScreen={() => {
            let newIndex = currentScreenIndex - 1;
            if (newIndex < 0) newIndex = 0;
            setCurrentScreenIndex(newIndex);
          }}
          backButton={renderBackButton()}
          hideSettings
          hideVolumeSlider={(expertCtx?.volume ?? -1) < 0}
        />
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  // TODO
  if (meeting.state === MeetingState.STOPPED || meeting.state === MeetingState.IDLE) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.content} style={{ justifyContent: "center" }}></div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer} style={darkMode ? { backgroundColor: "#222222" } : undefined}>
      {isSpectator(session) ? <div className={styles.spectatorBar}>Spectateur</div> : null}
      <div className={styles.content}>{renderOutletOrRedirect()}</div>
      {renderBottomBar(darkMode)}
      {renderDebugOutgoingStream()}
      {meeting?.state === MeetingState.PAUSED ? (
        <PauseView
          mode="expert"
          meeting={meeting}
          onResume={async () => {
            await updateMeetingState(MeetingState.RUNNING);
          }}
          onStop={async () => {
            await stop();
          }}
        />
      ) : null}
      <DevSoundIndicatorsBanner meetingData={populatedData} canHearSession={(session) => canHearSession(meetingAPI, session)} />
    </div>
  );
};
